import { Pagination, PaginationState } from '@innovyze/lib-am-common';
import { StatusType } from '@innovyze/stylovyze';

interface InspectionConditionScore {
	condition_category: string;
	operator: string;
	values: number[];
}

export interface Inspections {
	condition_score?: InspectionConditionScore;
	defect_codes?: string[];
}

interface TaskOrEvent {
	type: string;
	date_field: string;
}

export interface CreateFailureDefinitionRequest {
	name: string;
	system_type: string;
	asset_type: string;
	inspection_standard_type: string;
	inspections?: Inspections;
	tasks?: TaskOrEvent[];
	events?: TaskOrEvent[];
	prioritized_category?: string;
	published?: boolean;
	status?: string;
}

export const failureDefinitionStatusToStatusType = (status?: string): StatusType => {
	if (!status) return StatusType.failed;

	switch (status.toLowerCase()) {
		case 'completed':
			return StatusType.complete;
		case 'running':
		case 'in_progress':
		case 'in progress':
		case 'processing':
		case 'pending':
			return StatusType.inProgress;
		case 'failed':
		case 'error':
		case 'failure':
			return StatusType.failed;
		case 'cancelled':
		case 'canceled':
		case 'stopped':
			return StatusType.cancelled;
		default:
			return StatusType.failed;
	}
};

export interface FailureDefinitionApiResponse {
	id: string;
	tenant_id: string;
	name: string;
	system_type: string;
	asset_type: string;
	inspection_standard: string;
	events?: {
		type: string;
		date_field: string;
	}[];
	inspections?: {
		condition_score?: {
			condition_category: string;
			operator: string;
			values: number[];
		};
		defect_codes?: string[];
	};
	tasks?: {
		type: string;
		date_field: string;
	}[];
	last_run_id?: string;
	updated_by?: string;
	updated_at?: string;
	created_at?: string;
	prioritized_category?: string | null;
	status?: string;
	last_run_at?: string;
	last_run_by?: string;
	asset_count?: number;
	published?: boolean;
}

export interface FailureDefinition {
	id: string;
	name: string;
	systemType: string;
	assetType: string;
	inspectionStandard: string;
	lastRunId?: string;
	lastRunAt?: string;
	status?: StatusType;
	runBy?: string;
	createdAt?: string;
	updatedAt?: string;
	assetCount?: number;
	events?: {
		type: string;
		date_field: string;
	}[];
	inspections?: {
		condition_score?: {
			condition_category: string;
			operator: string;
			values: number[];
		};
		defect_codes?: string[];
	};
	tasks?: {
		type: string;
		date_field: string;
	}[];
	prioritizedCategory?: string | null;
	published?: boolean;
	tenantId?: string;
}

export interface GetFailureDefinitionResponse {
	failure_definition: FailureDefinitionApiResponse;
}

export interface GetAllFailureDefinitionsResponse {
	failure_definitions: FailureDefinitionApiResponse[];
}

export interface FailureDefinitionStoreState {
	inProcess: boolean;
	successful: boolean | null;
	id: string | null;
	run: {
		inProcess: boolean;
		successful: boolean | null;
	};

	allFailureDefinitionsList: {
		inProcess: boolean;
		successful: boolean | null;
		failureDefinitions: FailureDefinition[];
	};
	failureDefinition: {
		inProcess: boolean;
		successful: boolean | null;
		failureDefinition: null | FailureDefinition;
	};
	failureDefinitionRunResults: PaginationState;
	paginationState: Record<string, GetFailureDefinitionRunResultsRequest>;
}

export interface FailureInfoEmbeddedDocApiResponse {
	failure_type: string;
	failure_detail: string;
	failure_date: string;
}

export interface FailureDefinitionRunResultApiResponse {
	id?: string;
	tenant_id: string;
	asset_id: string;
	run_id: string;
	query_id: string;
	run_by: string;
	completed_at: string;
	failure_type: string;
	failure_detail: string;
	failure_date: string;
	installation_date?: string;
	age_at_failure?: number;
	all_failures: FailureInfoEmbeddedDocApiResponse[];
}

export interface GetFailureDefinitionRunResultsResponse {
	pagination: Pagination;
	data: FailureDefinitionRunResultApiResponse[];
}

export interface Filter {
	field: string;
	operator: string;
	value: string | string[];
}

export interface GetFailureDefinitionRunResultsRequest {
	failureDefinitionId: string;
	runId: string;
	orderBy: string;
	order: number; // 1 for ascending, -1 for descending
	filters?: Filter[] | null;
	filter_logistic_operator?: 'and' | 'or';
	searchText?: string | null;
	page: number;
	pageSize: number;
	filterModel: {
		items: Array<{
			id?: string | number;
			field: string;
			operator: string;
			value?: string | string[] | null;
		}>;
		logicOperator?: string;
	} | null;
}

export interface FailureInfoEmbeddedDoc {
	failureType: string;
	failureDetail: string;
	failureDate: string;
}

export interface FailureDefinitionRunResult {
	id?: string;
	tenantId: string;
	assetId: string;
	runId: string;
	queryId: string;
	runBy: string;
	completedAt: string;
	failureType: string;
	failureDetail: string;
	failureDate: string;
	installationDate?: string;
	ageAtFailure?: number;
	allFailures: FailureInfoEmbeddedDoc[];
}
